import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Info, Plus } from 'lucide-react';
import { ConsultationModeRow } from './consultation-mode-row';

// The per-queue Consultation modes tab: an explainer banner, a sortable list of consultation
// modes, and an add affordance. Mirrors EntryTypesSection's structure/pattern exactly.
export function ConsultationModesSection({ consultationModes, disabled = false, onChange, onDelete, onAdd, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = consultationModes.map((m) => m.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) onReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2.5 rounded-lg bg-primary/10 px-4 py-3 ring-1 ring-primary/25">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="text-[12.5px] leading-relaxed text-foreground">
          Consultation modes describe how a visit happens (in person, video call, phone, …).{' '}
          <b>Requires link</b> marks modes that need a join link generated for the patient.
        </p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {consultationModes.map((mode) => (
              <ConsultationModeRow
                key={mode.id}
                mode={mode}
                disabled={disabled}
                onChange={(patch) => onChange(mode.id, patch)}
                onDelete={() => onDelete(mode.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {consultationModes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
          No consultation modes yet. Add one to describe how visits in this queue happen.
        </div>
      ) : null}

      {!disabled ? (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex w-fit items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-[12.5px] font-semibold text-muted-foreground transition hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add mode
        </button>
      ) : null}
    </div>
  );
}
